import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Users, Plus, Minus, Send, Loader2, CheckCircle2, Clock, 
  AlertTriangle, ExternalLink, RefreshCw, Save, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  getCrewRequirements,
  updateCrewRequirements,
  getWorkOrderCrewRequest,
  queueCrewRequest
} from '../../services/api';

const RequestCrewCard = ({ workOrderId, workOrder, onUpdate }) => {
  const [requirements, setRequirements] = useState({
    crew_leader: 1,
    mechanic_a: 1,
    mechanic_b: 0,
    helper: 0,
    construction_worker: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [crewRequest, setCrewRequest] = useState(null);
  const [mode, setMode] = useState('DRAFT');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalRequirements, setOriginalRequirements] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch crew requirements
      const reqRes = await getCrewRequirements(workOrderId);
      if (reqRes.data.crew_requirements && Object.keys(reqRes.data.crew_requirements).length > 0) {
        const reqs = {
          crew_leader: reqRes.data.crew_requirements.crew_leader || 1,
          mechanic_a: reqRes.data.crew_requirements.mechanic_a || 1,
          mechanic_b: reqRes.data.crew_requirements.mechanic_b || 0,
          helper: reqRes.data.crew_requirements.helper || 0,
          construction_worker: reqRes.data.crew_requirements.construction_worker || 0
        };
        setRequirements(reqs);
        setOriginalRequirements(reqs);
      } else {
        // Set defaults
        const defaults = { crew_leader: 1, mechanic_a: 1, mechanic_b: 0, helper: 0, construction_worker: 0 };
        setRequirements(defaults);
        setOriginalRequirements(defaults);
      }

      // Fetch active crew request
      const crRes = await getWorkOrderCrewRequest(workOrderId);
      if (crRes.data.crew_request) {
        setCrewRequest(crRes.data.crew_request);
        setMode(crRes.data.crew_request.mode || 'DRAFT');
      } else {
        setCrewRequest(null);
      }
    } catch (err) {
      console.error('Failed to fetch crew data:', err);
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Track changes
  useEffect(() => {
    if (originalRequirements) {
      const changed = Object.keys(requirements).some(
        key => requirements[key] !== originalRequirements[key]
      );
      setHasChanges(changed);
    }
  }, [requirements, originalRequirements]);

  const handleChange = (role, delta) => {
    setRequirements(prev => ({
      ...prev,
      [role]: Math.max(0, (prev[role] || 0) + delta)
    }));
  };

  const handleInputChange = (role, value) => {
    const numValue = parseInt(value) || 0;
    setRequirements(prev => ({
      ...prev,
      [role]: Math.max(0, numValue)
    }));
  };

  const handleSaveAsDraft = async () => {
    setSaving(true);
    try {
      await updateCrewRequirements(workOrderId, requirements);
      setOriginalRequirements({ ...requirements });
      setHasChanges(false);
      toast.success('Crew requirements saved');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to save crew requirements:', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleQueueRequest = async () => {
    const total = Object.values(requirements).reduce((a, b) => a + b, 0);
    if (total === 0) {
      toast.error('Please set crew requirements first');
      return;
    }

    setQueueing(true);
    try {
      // First save requirements
      await updateCrewRequirements(workOrderId, requirements);
      setOriginalRequirements({ ...requirements });
      
      // Then queue crew request
      const res = await queueCrewRequest(workOrderId, {
        mode: mode,
        notes: null
      });
      
      // Show warnings if any
      if (res.data.warnings && res.data.warnings.length > 0) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      
      toast.success(res.data.message || 'Crew request queued successfully');
      fetchData(); // Refresh to show updated status
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to queue crew request:', err);
      toast.error(err.response?.data?.detail || 'Failed to queue request');
    } finally {
      setQueueing(false);
    }
  };

  const handleReset = () => {
    setRequirements({
      crew_leader: 1,
      mechanic_a: 1,
      mechanic_b: 0,
      helper: 0,
      construction_worker: 0
    });
  };

  const totalFTE = Object.values(requirements).reduce((a, b) => a + b, 0);

  const roleLabels = [
    { key: 'crew_leader', label: 'Crew Leader', warning: true },
    { key: 'mechanic_a', label: 'A Mechanic', warning: true },
    { key: 'mechanic_b', label: 'B Mechanic', warning: false },
    { key: 'helper', label: 'Helper', warning: false },
    { key: 'construction_worker', label: 'Construction Worker', warning: false }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      queued: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock, label: 'QUEUED' },
      ready: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle2, label: 'READY' },
      started: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2, label: 'STARTED' },
      completed: { color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: CheckCircle2, label: 'COMPLETED' },
      cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle, label: 'CANCELLED' }
    };
    return badges[status] || badges.queued;
  };

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="request-crew-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-slate-100 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Request Crew (FTE Breakdown)
          </span>
          <button
            onClick={fetchData}
            className="p-1.5 hover:bg-slate-700 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-slate-400" />
          </button>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Active Crew Request Status Banner */}
        {crewRequest && (
          <div className={`p-3 rounded-lg border ${getStatusBadge(crewRequest.status).color}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {React.createElement(getStatusBadge(crewRequest.status).icon, { className: "w-4 h-4" })}
                <span className="font-medium text-sm">
                  Crew Request: {getStatusBadge(crewRequest.status).label}
                </span>
              </div>
              <a
                href={`/crew-requests`}
                className="text-xs flex items-center gap-1 hover:underline"
              >
                View Queue <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="mt-2 text-xs opacity-80">
              Mode: {crewRequest.mode} | FTE: {crewRequest.crew_size_total} | 
              Requested: {new Date(crewRequest.requested_at).toLocaleDateString()}
            </div>
          </div>
        )}

        {/* Role Inputs */}
        <div className="space-y-3">
          {roleLabels.map(({ key, label, warning }) => (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-slate-300 text-sm">{label}</span>
                {warning && requirements[key] === 0 && (
                  <AlertTriangle className="w-4 h-4 text-amber-400" title={`No ${label} assigned`} />
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleChange(key, -1)}
                  disabled={requirements[key] === 0}
                  className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="w-3.5 h-3.5 text-slate-300" />
                </button>
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={requirements[key]}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  className="w-12 text-center bg-slate-700 border border-slate-600 rounded px-2 py-1 text-slate-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => handleChange(key, 1)}
                  className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 text-slate-300" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Total FTE Display */}
        <div className="flex items-center justify-between py-3 border-t border-b border-slate-700">
          <span className="text-slate-200 font-medium">Total FTE</span>
          <span className={`text-2xl font-bold ${totalFTE < 2 ? 'text-amber-400' : 'text-blue-400'}`}>
            {totalFTE}
          </span>
        </div>

        {/* Warnings */}
        {totalFTE < 2 && (
          <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Total FTE is less than 2</span>
          </div>
        )}

        {/* Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-400 mb-2">Canvass Mode</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="crewMode"
                value="DRAFT"
                checked={mode === 'DRAFT'}
                onChange={(e) => setMode(e.target.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-slate-300 text-sm">Draft (Direct Assignment)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="crewMode"
                value="INTEREST"
                checked={mode === 'INTEREST'}
                onChange={(e) => setMode(e.target.value)}
                className="text-purple-600 focus:ring-purple-500"
              />
              <span className="text-slate-300 text-sm">Interest (Bidding)</span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2 pt-2">
          {/* Primary: Queue Crew Request */}
          <button
            onClick={handleQueueRequest}
            disabled={queueing || totalFTE === 0}
            className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="queue-crew-request-btn"
          >
            {queueing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {crewRequest ? 'Update Crew Request' : 'Queue Crew Request'}
          </button>
          
          {/* Secondary buttons row */}
          <div className="flex gap-2">
            {/* Save as Draft */}
            <button
              onClick={handleSaveAsDraft}
              disabled={saving || !hasChanges}
              className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="save-draft-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save as Draft
            </button>
            
            {/* Reset */}
            <button
              onClick={handleReset}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200 font-medium text-sm flex items-center justify-center gap-2 transition-colors"
              data-testid="reset-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>

        {/* Helper Text */}
        <p className="text-xs text-slate-500 text-center mt-2">
          This queues a crew request. A Supervisor/Admin must press <strong>Start Canvass</strong> in Quick Canvass to begin.
        </p>

        {/* Link to Queue (if request exists) */}
        {crewRequest && (
          <a
            href="/crew-requests"
            className="block w-full px-4 py-2 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 font-medium text-sm text-center flex items-center justify-center gap-2 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View in Quick Canvass Queue
          </a>
        )}
      </CardContent>
    </Card>
  );
};

export default RequestCrewCard;
